import React, { useState, useEffect, useCallback } from 'react';
import { registerPasskey, isWebAuthnSupported, listPasskeys } from '../api/webauthn';
import { Button } from './ui';

/**
 * Whether the current browser supports WebAuthn and whether the user already
 * has at least one passkey registered. Feeds the "Passkey einrichten" row of
 * the Suchen setup checklist (`SetupChecklist`).
 */
export function usePasskeyStatus() {
  const [supported, setSupported] = useState(false);
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);

  const refresh = useCallback(async () => {
    if (!isWebAuthnSupported()) {
      setSupported(false);
      setHasPasskey(false);
      return;
    }
    setSupported(true);
    try {
      const creds = await listPasskeys();
      setHasPasskey(creds.length > 0);
    } catch {
      setHasPasskey(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { supported, hasPasskey, refresh };
}

export interface PasskeySetupActionProps {
  onRegistered?: () => void;
}

/**
 * "Einrichten" action for the setup checklist's Passkey row. Same WebAuthn
 * registration flow (and error handling) as the former standalone banner.
 */
export const PasskeySetupAction: React.FC<PasskeySetupActionProps> = ({ onRegistered }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState('');

  const handleSetup = async () => {
    setError('');
    setIsRegistering(true);
    try {
      await registerPasskey();
      onRegistered?.();
    } catch (err: any) {
      if (err.name !== 'NotAllowedError') {
        // User cancelling the browser prompt is not an error worth surfacing.
        setError('Einrichtung fehlgeschlagen. Du kannst es später in den Einstellungen versuchen.');
      }
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <Button type="button" variant="secondary" size="sm" onClick={handleSetup} disabled={isRegistering}>
        {isRegistering ? 'Einrichten...' : 'Einrichten'}
      </Button>
      {error && <p className="text-xs text-danger-text">{error}</p>}
    </div>
  );
};
